import React, { useMemo, useCallback, useState } from 'react';
import { styled } from '@compiled/react';
import { Box } from '@atlaskit/primitives';
import Tabs, { type TabData, Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	fireUIAnalytics,
	SCREEN,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { JsmPageInteractiveEvent } from '@atlassian/jira-servicedesk-common/src/utils/browser-metric/index.tsx';
import EditableAttributeList from '@atlassian/jira-servicedesk-insight-editable-attribute-list/src/ui/index.tsx';
import { loadObjectDetailExperience } from '@atlassian/jira-servicedesk-insight-experiences/src/index.tsx';
import { useEditableAttributeTypes } from '@atlassian/jira-servicedesk-insight-object-details-store/src/controllers/store/index.tsx';
import { AsyncInsightObjectTimeline } from '@atlassian/jira-servicedesk-insight-object-timeline/src/async.tsx';
import { InsightObjectViewAttributesSkeleton } from '@atlassian/jira-skeletons/src/ui/insight-object/InsightObjectViewSkeleton.tsx';
import { ExperienceSuccess } from '@atlassian/ufo';
import messages from './messages.tsx';
import { type Props, ObjectDetailsRenderingContext } from './types.tsx';

export const ObjectDetails = (props: Props) => {
	const { context, isMultiColumn } = props;
	const [attributeTypes] = useEditableAttributeTypes();

	const [selectedTab, setSelectedTab] = useState<number>(0);
	const { formatMessage } = useIntl();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const tabItems = useMemo(
		() => [
			{
				label: formatMessage(messages.detailsTabTitle),
				content: (
					<TabContainer>
						<EditableAttributeList attributeTypes={attributeTypes} isMultiColumn={isMultiColumn} />
						{attributeTypes.length > 0 && context === ObjectDetailsRenderingContext.Page && (
							<ContextualAnalyticsData sourceName="cmdbObjectDetails" sourceType={SCREEN}>
								<JsmPageInteractiveEvent />
								<FireScreenAnalytics />
							</ContextualAnalyticsData>
						)}
						{attributeTypes.length > 0 && context === ObjectDetailsRenderingContext.Component && (
							<ExperienceSuccess experience={loadObjectDetailExperience} />
						)}
					</TabContainer>
				),
				testId: 'servicedesk-insight-object-details.ui.object-details-tabs.attributes',
			},
			{
				label: formatMessage(messages.activityTabTitle),
				content: (
					<TabContainer>
						<AsyncInsightObjectTimeline />
					</TabContainer>
				),
				testId: 'servicedesk-insight-object-details.ui.object-details-tabs.activity',
			},
		],
		[attributeTypes, context, formatMessage, isMultiColumn],
	);

	const onSelectTab = useCallback(
		(_: TabData, index: number) => {
			if (index === 0) {
				fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'insightObjectDetailsTab');
			}

			if (index === 1) {
				fireUIAnalytics(
					createAnalyticsEvent({}),
					'button clicked',
					'insightObjectActivityTimelineTab',
				);
			}

			setSelectedTab(index);
		},
		[setSelectedTab, createAnalyticsEvent],
	);

	const handleChange = (index: number) => {
		onSelectTab({}, index);
	};

	if (attributeTypes.length === 0) {
		return <InsightObjectViewAttributesSkeleton />;
	}
	return (
		<Box paddingInline="space.100">
			<Tabs
				selected={selectedTab}
				onChange={handleChange}
				id="object-details-panel-tabs"
				shouldUnmountTabPanelOnChange
			>
				<TabList>
					{tabItems.map((tab, index) => (
						<Tab testId={tab.testId} key={index}>
							{tab.label}
						</Tab>
					))}
				</TabList>
				{tabItems.map((tab, index) => (
					<TabPanel key={index}>{tab.content}</TabPanel>
				))}
			</Tabs>
		</Box>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TabContainer = styled.div({
	marginTop: token('space.300'),
	width: '100%',
});
